import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiUpload } from './client';
import type { Message, MessageListResponse, UnreadCount } from '@spi/shared';

export function useAskMessages(askId: number) {
  return useQuery({
    queryKey: ['messages', 'ask', askId],
    queryFn: () => apiFetch<MessageListResponse>(`/api/asks/${askId}/messages`),
    enabled: askId > 0,
  });
}

export function usePostAskMessage(askId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, files }: { body: string; files?: File[] }) => {
      const fd = new FormData();
      fd.append('body', body);
      if (files) files.forEach((f) => fd.append('files', f));
      return apiUpload<Message>(`/api/asks/${askId}/messages`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages', 'ask', askId] });
      qc.invalidateQueries({ queryKey: ['unreadCounts'] });
    },
  });
}

export function useDonationMessages(donationId: number) {
  return useQuery({
    queryKey: ['messages', 'donation', donationId],
    queryFn: () => apiFetch<MessageListResponse>(`/api/transactions/${donationId}/messages`),
    enabled: donationId > 0,
  });
}

export function usePostDonationMessage(donationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, files }: { body: string; files?: File[] }) => {
      const fd = new FormData();
      fd.append('body', body);
      if (files) files.forEach((f) => fd.append('files', f));
      return apiUpload<Message>(`/api/transactions/${donationId}/messages`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages', 'donation', donationId] });
      qc.invalidateQueries({ queryKey: ['unreadCounts'] });
    },
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: number) =>
      apiFetch<{ message: string }>(`/api/messages/${messageId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contextType, contextId }: { contextType: string; contextId: number }) =>
      apiFetch<{ message: string }>('/api/messages/mark-read', {
        method: 'POST',
        body: JSON.stringify({ contextType, contextId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unreadCounts'] });
    },
  });
}

export function useUnreadCounts() {
  return useQuery({
    queryKey: ['unreadCounts'],
    queryFn: () => apiFetch<UnreadCount[]>('/api/messages/unread'),
    refetchInterval: 60_000,
  });
}
